import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../../css/Tools/Ai.css";
import {
  getUserProfileData,
  getUserFinancialData,
} from "../../firebase/firebaseService";
import { auth } from "../../firebase/firebase";
import { GoogleGenerativeAI } from "@google/generative-ai";
import rob1 from "../../assets/header-images/rob1.jpg";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { TypeAnimation } from "react-type-animation";
import { searchDocuments } from "../../config/pinecone";

const API_KEY = process.env.REACT_APP_GEMINI_APIKEY;
const genAI = new GoogleGenerativeAI(API_KEY);
const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash-8b" });

function ChatbotModal() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      message: `Hi there! 👋 I'm  your friendly college application assistant. I specialize in helping students like you with every step of the college journey. Whether you need advice on choosing majors, building your school list, chancing your admissions, brainstorming essays, finding scholarships, or anything else—I'm here to help!

Just type your question or topic below. You can also check out CollegeNav's resources for more info 😊`,
      direction: "incoming",
      sender: "Aza",
      isMarkdown: true,
      showCapabilitiesButton: true,
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [inputText, setInputText] = useState("");
  const [currentTypingMessage, setCurrentTypingMessage] = useState("");
  const [isShowingTypedResponse, setIsShowingTypedResponse] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    {
      id: 1,
      title: "College Application Help",
      timestamp: "Today",
      messages: [],
    },
  ]);
  const [currentChatId, setCurrentChatId] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState(false);
  const abortController = useRef(null);
  const messagesEndRef = useRef(null);

  const handleStop = () => {
    if (abortController.current) {
      abortController.current.abort();
      setIsTyping(false);
    }
  };

  const handleSend = async () => {
    if (inputText.trim() === "") return;

    const newMessage = {
      message: inputText,
      direction: "outgoing",
      sender: "user",
      isMarkdown: true,
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputText("");
    setIsTyping(true);

    await processMessageToGemini(inputText);
  };

  const processMessageToGemini = async (userInput) => {
    if (userInput.trim() === "") return;

    abortController.current = new AbortController();

    try {
      let personalizedMessage = `${userInput}\n\n`;

      if (userData) {
        personalizedMessage += `
Background Information:
- **Name**: ${userData?.name || "Unknown"}
- **Gender**: ${userData?.gender || "Unknown"}
- **Date of Birth**: ${userData?.dateOfBirth || "Unknown"}
- **University**: ${userData?.university || "Unknown"}
- **Major**: ${userData?.major || "Unknown"}
- **Educational Status**: ${userData?.educationalStatus || "Unknown"}
- **GPA**: ${userData?.gpa || "Unknown"}
- **Career Goal**: ${userData?.careerGoal || "Unknown"}
- **Location**: ${userData?.location?.city || "Unknown"}, ${
          userData?.location?.state || "Unknown"
        }
- **Race**: ${userData?.race || "Unknown"}
- **Demographics**: ${userData?.demographics?.join(", ") || "None"}
`;
      }

      if (financialData) {
        personalizedMessage += `
Financial Information:
- **Household Income**: ${financialData?.householdIncome || "Unknown"}
- **Financial Aid (FAFSA)**: ${financialData?.financialAid?.fafsa || "Unknown"}
- **State Grant**: ${financialData?.financialAid?.stateGrant || "Unknown"}
- **Scholarship**: ${financialData?.financialAid?.scholarship || "Unknown"}
- **Loan**: ${financialData?.financialAid?.loan || "Unknown"}
`;
      }

      // Search for relevant documents from Pinecone
      let relevantContext = "";
      try {
        console.log("🔍 Searching documents for:", userInput);
        const similarDocs = await searchDocuments(userInput, 3);

        if (similarDocs.length > 0) {
          relevantContext = `\n\n📚 **Relevant Information from Knowledge Base:**\n${similarDocs
            .map(
              (doc, index) =>
                `${index + 1}. ${doc.content.substring(0, 500)}${
                  doc.content.length > 500 ? "..." : ""
                }`
            )
            .join("\n\n")}`;

          console.log("✅ Found relevant documents:", similarDocs.length);
        } else {
          console.log("ℹ️ No relevant documents found");
        }
      } catch (error) {
        console.error("❌ Error searching documents:", error);
      }

      // Build conversation context from recent messages
      const conversationHistory = messages
        .filter((msg) => !msg.showCapabilitiesButton)
        .slice(-8)
        .map((msg) => {
          const role = msg.direction === "outgoing" ? "User" : "Aza";
          return `${role}: ${msg.message
            .replace(/\*\*(.*?)\*\*/g, "$1")
            .replace(/<\/?b>/g, "")}`;
        })
        .join("\n");

      const prompt = `You are a friendly, knowledgeable, and supportive AI assistant who helps students with all aspects of the college application process. 

IMPORTANT: Keep responses SHORT and CONCISE (2-4 sentences max). Provide quick, actionable advice.

Your job is to:
- Provide brief, clear, and personalized advice for college-related questions
- Use bullet points for lists (max 3-4 points)
- Be encouraging but concise
- Suggest specific next steps
- Remember and reference previous parts of our conversation when relevant
- **PRIORITIZE information from the Knowledge Base when available** - this contains the most accurate and detailed information

Student Profile Information:
${personalizedMessage}

Previous Conversation Context:
${conversationHistory || "This is the start of our conversation."}

${relevantContext}

Current Question: ${userInput}

Guidelines:
- Maximum 2-4 sentences
- Be direct and actionable
- Use simple language
- End with one encouraging phrase
- If listing items, use max 3-4 bullet points
- Reference previous conversation when helpful
- **Use Knowledge Base information first** - it's the most reliable source
- If Knowledge Base has relevant info, mention it's from your "knowledge base" or "resources"
`;

      const result = await model.generateContent(
        {
          contents: [{ role: "user", parts: [{ text: prompt }] }],
        },
        { signal: abortController.current.signal }
      );

      const responseText = await result.response.text();

      // Start typing animation
      setIsTyping(false);
      setIsShowingTypedResponse(true);
      setCurrentTypingMessage(responseText);
    } catch (error) {
      if (error.name === "AbortError") {
        setMessages((prev) => [
          ...prev,
          {
            message: "Response stopped by user.",
            direction: "incoming",
            sender: "Aza",
            isMarkdown: false,
          },
        ]);
      } else {
        console.error("Error:", error);
        setMessages((prev) => [
          ...prev,
          {
            message: "Sorry, I encountered an error. Please try again.",
            direction: "incoming",
            sender: "Aza",
            isMarkdown: false,
          },
        ]);
      }
      setIsTyping(false);
      abortController.current = null;
    }
  };

  const handleTypingComplete = () => {
    setMessages((prev) => [
      ...prev,
      {
        message: currentTypingMessage,
        direction: "incoming",
        sender: "Aza",
        isMarkdown: true,
      },
    ]);
    setIsShowingTypedResponse(false);
    setCurrentTypingMessage("");
  };

  const toggleCapabilities = () => {
    setShowCapabilities(!showCapabilities);
  };

  const startNewChat = () => {
    const newChatId = Date.now();
    const newChat = {
      id: newChatId,
      title: "New Chat",
      timestamp: "Now",
      messages: [
        {
          message: `Hi there! 👋 I'm your friendly college application assistant. I specialize in helping students like you with every step of the college journey. Whether you need advice on choosing majors, building your school list, chancing your admissions, brainstorming essays, finding scholarships, or anything else—I'm here to help!

Just type your question or topic below. You can also check out CollegeNav's resources for more info. 😊`,
          direction: "incoming",
          sender: "Aza",
          isMarkdown: true,
          showCapabilitiesButton: true,
        },
      ],
    };

    setChatHistory((prev) => [newChat, ...prev]);
    setCurrentChatId(newChatId);
    setMessages(newChat.messages);
    setShowCapabilities(false);
    setSidebarOpen(false);
  };

  const switchToChat = (chatId) => {
    const chat = chatHistory.find((c) => c.id === chatId);
    if (chat) {
      setCurrentChatId(chatId);
      setMessages(chat.messages);
      setSidebarOpen(false);
    }
  };

  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    setChatHistory((prev) => prev.filter((chat) => chat.id !== chatId));
    if (currentChatId === chatId && chatHistory.length > 1) {
      const remainingChats = chatHistory.filter((chat) => chat.id !== chatId);
      if (remainingChats.length > 0) {
        switchToChat(remainingChats[0].id);
      } else {
        startNewChat();
      }
    }
  };

  const updateCurrentChatTitle = (newTitle) => {
    setChatHistory((prev) =>
      prev.map((chat) =>
        chat.id === currentChatId ? { ...chat, title: newTitle } : chat
      )
    );
  };

  // Update chat history when messages change
  useEffect(() => {
    if (messages.length > 1) {
      // More than just the initial message
      setChatHistory((prev) =>
        prev.map((chat) =>
          chat.id === currentChatId
            ? {
                ...chat,
                messages,
                title:
                  chat.title === "New Chat"
                    ? messages
                        .find((m) => m.direction === "outgoing")
                        ?.message.slice(0, 30) + "..." || "New Chat"
                    : chat.title,
              }
            : chat
        )
      );
    }
  }, [messages, currentChatId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [messages, isShowingTypedResponse]);

  // Prevent body scroll when component mounts
  useEffect(() => {
    // Prevent body scroll and ensure high z-index
    document.body.style.overflow = "hidden";
    document.body.style.position = "relative";
    document.body.style.zIndex = "1";

    return () => {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.zIndex = "unset";
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser) {
        navigate("/login-prompt");
        return;
      }

      setLoading(true);
      try {
        const [userProfileData, userFinancialData] = await Promise.all([
          getUserProfileData(),
          getUserFinancialData(),
        ]);
        setUserData(userProfileData);
        setFinancialData(userFinancialData);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Custom markdown renderers
  const markdownComponents = {
    // Custom renderer for bold text
    b: ({ children }) => <strong className="markdown-bold">{children}</strong>,
    strong: ({ children }) => (
      <strong className="markdown-bold">{children}</strong>
    ),

    // Custom renderer for paragraphs
    p: ({ children }) => <p className="markdown-paragraph">{children}</p>,

    // Custom renderer for lists
    ul: ({ children }) => <ul className="markdown-list">{children}</ul>,
    ol: ({ children }) => <ol className="markdown-list">{children}</ol>,
    li: ({ children }) => <li className="markdown-list-item">{children}</li>,

    // Custom renderer for headings
    h1: ({ children }) => <h1 className="markdown-heading">{children}</h1>,
    h2: ({ children }) => <h2 className="markdown-heading">{children}</h2>,
    h3: ({ children }) => <h3 className="markdown-heading">{children}</h3>,
  };

  return (
    <div id="aza-body">
      {/* Mobile sidebar toggle */}
      <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}>
        ☰
      </button>

      {/* Sidebar overlay for mobile */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "show" : ""}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      <div className="outer-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-header">
            <button className="new-chat-btn" onClick={startNewChat}>
              <span>➕</span>
              <span>New chat</span>
            </button>
          </div>

          <div className="sidebar-content">
            <div className="history-section">
              <h3>Recent</h3>
              {chatHistory.slice(0, 5).map((chat) => (
                <div
                  key={chat.id}
                  className={`history-item ${
                    chat.id === currentChatId ? "active" : ""
                  }`}
                  onClick={() => switchToChat(chat.id)}
                >
                  <div className="history-item-icon">💬</div>
                  <div className="history-item-text">{chat.title}</div>
                  <div className="history-item-actions">
                    <button
                      className="history-action-btn"
                      onClick={(e) => deleteChat(chat.id, e)}
                      title="Delete chat"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {chatHistory.length > 5 && (
              <div className="history-section">
                <h3>Older</h3>
                {chatHistory.slice(5).map((chat) => (
                  <div
                    key={chat.id}
                    className={`history-item ${
                      chat.id === currentChatId ? "active" : ""
                    }`}
                    onClick={() => switchToChat(chat.id)}
                  >
                    <div className="history-item-icon">💬</div>
                    <div className="history-item-text">{chat.title}</div>
                    <div className="history-item-actions">
                      <button
                        className="history-action-btn"
                        onClick={(e) => deleteChat(chat.id, e)}
                        title="Delete chat"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main chat area */}
        <div className="chat-window-container">
          <div className="chat-window">
            <div className="message-list">
              {messages.map((message, i) => (
                <div
                  key={i}
                  className={`message ${
                    message.direction === "outgoing"
                      ? "user-message"
                      : "bot-message"
                  }`}
                >
                  <div className="message-avatar">
                    {message.direction === "outgoing" ? "U" : "A"}
                  </div>
                  <div className="message-content">
                    <div className="message-bubble">
                      <div className="message-text">
                        {message.isMarkdown ? (
                          <ReactMarkdown
                            components={markdownComponents}
                            rehypePlugins={[rehypeRaw, rehypeSanitize]}
                            allowedElements={[
                              "p",
                              "br",
                              "strong",
                              "b",
                              "em",
                              "i",
                              "u",
                              "h1",
                              "h2",
                              "h3",
                              "h4",
                              "h5",
                              "h6",
                              "ul",
                              "ol",
                              "li",
                              "a",
                              "code",
                              "pre",
                              "blockquote",
                            ]}
                          >
                            {message.message}
                          </ReactMarkdown>
                        ) : (
                          <span>{message.message}</span>
                        )}
                      </div>

                      {/* Capabilities Button */}
                      {message.showCapabilitiesButton && (
                        <div className="capabilities-section">
                          <button
                            className="capabilities-toggle-btn"
                            onClick={toggleCapabilities}
                          >
                            <span className="capabilities-icon">
                              {showCapabilities ? "▼" : "▶"}
                            </span>
                            What can I help you with?
                          </button>

                          {showCapabilities && (
                            <div className="capabilities-content">
                              <div className="capabilities-grid">
                                <div className="capability-item">
                                  <span className="capability-icon">🎓</span>
                                  <span className="capability-text">
                                    Exploring Majors & Careers
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">🏫</span>
                                  <span className="capability-text">
                                    Building Your School List
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">📊</span>
                                  <span className="capability-text">
                                    Chancing Your Acceptance
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">✍️</span>
                                  <span className="capability-text">
                                    Essay Writing & Brainstorming
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">🏆</span>
                                  <span className="capability-text">
                                    Extracurriculars & Activities
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">📅</span>
                                  <span className="capability-text">
                                    Application Timelines & Roadmaps
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">💰</span>
                                  <span className="capability-text">
                                    Scholarships & Financial Aid
                                  </span>
                                </div>
                                <div className="capability-item">
                                  <span className="capability-icon">🚀</span>
                                  <span className="capability-text">
                                    Early Career Resources
                                  </span>
                                </div>
                              </div>
                              <p className="capabilities-footer">
                                Or any other college-related question!
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="message bot-message">
                  <div className="message-avatar">A</div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span className="typing-text">Thinking</span>
                      <div className="typing-dots">
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isShowingTypedResponse && (
                <div className="message bot-message">
                  <div className="message-avatar">A</div>
                  <div className="message-content">
                    <div className="message-bubble">
                      <div className="message-text">
                        <TypeAnimation
                          sequence={[
                            currentTypingMessage,
                            () => handleTypingComplete(),
                          ]}
                          wrapper="div"
                          speed={90}
                          cursor={true}
                          repeat={0}
                          omitDeletionAnimation={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="input-container">
            <div className="input-wrapper">
              <textarea
                className="input-field"
                placeholder="Message assistant..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (inputText.trim()) handleSend();
                  }
                }}
                rows={1}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height =
                    Math.min(e.target.scrollHeight, 200) + "px";
                }}
              />
              <button
                className={`send-button ${isTyping ? "stop-button" : ""}`}
                onClick={isTyping ? handleStop : handleSend}
                disabled={!inputText.trim() && !isTyping}
                title={isTyping ? "Stop" : "Send"}
              >
                {isTyping ? "⏹" : "↑"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatbotModal;
